import { PencilIcon } from "lucide-react";
import { useEffect, useState } from "react";
import { Button, cn, Input } from "web-design-system";
import { CopyButton } from "@core/components/copy-btn";

type EditableLinkProps = {
  label: string;
  /** The full link, shown while it is not being edited */
  url: string;
  /** The editable part of the link */
  value: string;
  /** Fixed parts of the link, shown around the field while editing */
  prefix?: string;
  suffix?: string;
  copyText: string;
  /** Throw an Error with a user facing message to show it below the field */
  onSave?: (value: string) => Promise<void>;
};

export function EditableLink({
  label,
  url,
  value,
  prefix,
  suffix,
  copyText,
  onSave,
}: EditableLinkProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const [error, setError] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (!isEditing) setDraft(value);
  }, [value, isEditing]);

  const cancel = () => {
    setDraft(value);
    setError("");
    setIsEditing(false);
  };

  const save = async () => {
    if (draft === value) {
      cancel();
      return;
    }
    setIsSaving(true);
    try {
      await onSave(draft);
      setIsEditing(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to update link");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="flex flex-col gap-3 rounded-lg bg-card p-5">
      <span className="font-medium text-sm">{label}</span>
      {isEditing ? (
        <div className="flex flex-col gap-1">
          <div className="flex items-center justify-between gap-3">
            <div
              className={cn(
                "flex min-w-0 items-center rounded-lg border border-input bg-background px-3 h-10 text-sm focus-within:border-primary",
                error && "border-destructive focus-within:border-destructive"
              )}
            >
              {prefix && (
                <span className="truncate text-muted-foreground">{prefix}</span>
              )}
              <Input
                autoFocus
                value={draft}
                disabled={isSaving}
                aria-label={label}
                aria-invalid={!!error}
                className="h-full w-36 min-w-12 border-0 px-0 shadow-none !ring-0 !outline-none"
                onChange={(e) => {
                  setDraft(e.target.value.trim());
                  setError("");
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    save();
                  } else if (e.key === "Escape") {
                    cancel();
                  }
                }}
              />
              {suffix && (
                <span className="shrink-0 text-muted-foreground">{suffix}</span>
              )}
            </div>
            <div className="flex shrink-0 gap-2">
              <Button type="button" disabled={isSaving} onClick={save}>
                {isSaving ? "Saving..." : "Save"}
              </Button>
              <Button
                type="button"
                variant="outline"
                disabled={isSaving}
                onClick={cancel}
              >
                Cancel
              </Button>
            </div>
          </div>
          {error && <span className="text-xs text-destructive">{error}</span>}
        </div>
      ) : (
        <div className="flex items-center gap-1 rounded-lg border border-input bg-background pl-3 pr-1 h-10 text-sm">
          <span className="flex-1 truncate">{url}</span>
          {onSave && (
            <Button
              type="button"
              size="icon"
              variant="ghost"
              aria-label={`Edit ${label.toLowerCase()}`}
              onClick={() => setIsEditing(true)}
            >
              <PencilIcon />
            </Button>
          )}
          <CopyButton text={copyText} variant="ghost" />
        </div>
      )}
    </div>
  );
}
